import React from "react";
import {
  Card,
  Chip,
  Divider,
  Grid,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";

const TransactionReportTable = ({ report }) => {
  const {
    placeData,
    periods = [],
    rateTotals = {},
    totalReservation = 0,
    totalRevenue = 0,
    serviceFee = 0,
    netRevenue = 0,
    startDate,
    transactionType = "daily",
  } = report;

  const amountToShow = (val) => (Number(val || 0) / 100).toFixed(2);

  // Helper to format rate names for display (USD_5 -> $5)
  const formatRateName = (name) => {
    if (typeof name === "string" && name.startsWith("USD_")) {
      return "$" + name.slice(4);
    }
    return name;
  };

  // Header Logic
  const isPass = placeData?.isPass;
  const totalLabel = isPass ? "Total Passes" : "Total Reservations";

  // Date & Label Logic
  const reportLabel =
    transactionType.charAt(0).toUpperCase() +
    transactionType.slice(1) +
    " Report";

  let dateDisplay = moment(startDate).format("MM/DD/YYYY");
  let weeklyDayLabel = "";

  if (transactionType === "weekly") {
    const end = moment(startDate).add(6, "days");
    dateDisplay = `${moment(startDate).format("MM/DD/YYYY")} - ${end.format("MM/DD/YYYY")}`;
    weeklyDayLabel = ` (${moment(startDate).format("dddd")})`;
  } else if (transactionType === "monthly") {
    const end = moment(startDate).endOf("month");
    dateDisplay = `${moment(startDate).format("MM/DD/YYYY")} - ${end.format("MM/DD/YYYY")}`;
  }

  // --- PDF GENERATION ---
  const downloadPdf = () => {
    if (!report) return;

    const doc = new jsPDF({ orientation: "landscape", format: "a3" });

    // 1. Header & Meta Data
    doc.setFontSize(18);
    doc.text(`${placeData?.parkingCode || "Unknown"} - ${reportLabel}`, 14, 20);

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Location: ${placeData?.locationAddress || "N/A"}`, 14, 26);
    doc.text(`Period: ${dateDisplay}${weeklyDayLabel}`, 14, 31);

    // 2. Synopsis Section
    doc.setDrawColor(220);
    doc.line(14, 36, 400, 36);
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.text("Synopsis:", 14, 44);

    const metrics = [
      {
        label: "Gross:",
        value: `$${amountToShow(totalRevenue)}`,
        color: [52, 71, 103],
      },
      {
        label: "Net:",
        value: `$${amountToShow(netRevenue)}`,
        color: [76, 175, 80],
      },
      {
        label: "Fees:",
        value: `$${amountToShow(serviceFee)}`,
        color: [26, 115, 232],
      },
      {
        label: `${totalLabel}:`,
        value: `${totalReservation}`,
        color: [233, 30, 99],
      },
    ];

    let xOffset = 35;
    doc.setFontSize(10);

    metrics.forEach((m) => {
      doc.setTextColor(0);
      doc.text(m.label, xOffset, 44);

      const labelWidth = doc.getTextWidth(m.label);
      doc.setTextColor(m.color[0], m.color[1], m.color[2]);
      doc.setFont(undefined, "bold");
      doc.text(m.value, xOffset + labelWidth + 2, 44);
      doc.setFont(undefined, "normal");

      xOffset += 60;
    });

    // 3. Table Data Preparation
    const header = [
      "Rate",
      ...periods.map((p) => p.time),
      totalLabel,
      "Gross Revenue",
    ];

    // A. Rate Rows
    const body = Object.entries(rateTotals).map(([label, meta]) => {
      const row = [formatRateName(label)];
      periods.forEach((p) => row.push(p?.rates?.[label]?.count ?? 0));
      row.push(meta?.count ?? 0);
      row.push(`$${amountToShow(meta?.totalSum)}`);
      return row;
    });

    // B. Summary Rows
    const totalsCountRow = [
      totalLabel,
      ...periods.map((p) => Number(p?.totalCount ?? 0)),
      totalReservation,
      "",
    ];

    const percentCountRow = [
      `% of ${totalLabel}`,
      ...periods.map((p) => `${Number(p?.percentage ?? 0).toFixed(2)}%`),
      "",
      "",
    ];

    const totalRevenueRow = [
      "Total Revenue",
      ...periods.map((p) => `$${amountToShow(p?.totalRevenue)}`),
      "",
      `$${amountToShow(totalRevenue)}`,
    ];

    const percentValueRow = [
      "% of Total $ Value",
      ...periods.map((p) => `${Number(p?.revenuePercent ?? 0).toFixed(2)}%`),
      "",
      "",
    ];

    const serviceFeeRow = [
      "Total Service Fee",
      ...periods.map((p) => `$${amountToShow(p?.totalServiceFee)}`),
      "",
      `$${amountToShow(serviceFee)}`,
    ];

    const netRevenueRow = [
      "Net Revenue",
      ...periods.map(
        (p) =>
          `$${amountToShow((p?.totalRevenue ?? 0) - (p?.totalServiceFee ?? 0))}`,
      ),
      "",
      `$${amountToShow(netRevenue)}`,
    ];

    const tableRows = [
      ...body,
      totalsCountRow,
      percentCountRow,
      totalRevenueRow,
      percentValueRow,
      serviceFeeRow,
      netRevenueRow,
    ];

    // C. Generate Table
    autoTable(doc, {
      head: [header],
      body: tableRows,
      startY: 50,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 1.5,
        valign: "middle",
        lineColor: [224, 224, 224],
      },
      headStyles: {
        fillColor: [240, 242, 245],
        textColor: [52, 71, 103],
        fontStyle: "bold",
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: 35 },
        [header.length - 1]: { fontStyle: "bold", halign: "right" },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index > 0) {
          data.cell.styles.halign = "right";
        }

        if (data.section === "body") {
          const firstCell = data.row.raw[0];

          if (firstCell === "Total Revenue") {
            data.cell.styles.textColor = [46, 125, 50];
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [232, 245, 233];
          }
          if (firstCell === "Net Revenue") {
            data.cell.styles.textColor = [21, 101, 192];
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [227, 242, 253];
          }
          if (firstCell === "Total Service Fee") {
            data.cell.styles.textColor = [100, 100, 100];
          }
          if (data.column.index === 0) {
            data.cell.styles.textColor = [52, 71, 103];
          }
        }
      },
    });

    const fileName = `${reportLabel}-${placeData?.parkingCode}-${moment(startDate).format("MM-DD-YYYY")}.pdf`;
    doc.save(fileName);
  };

  // --- MINIMAL UI COMPONENTS ---
  const SummaryCard = ({ icon, title, value, color }) => (
    <Card sx={{ height: "100%", boxShadow: 2, overflow: "visible" }}>
      <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
        <MDBox
          variant="gradient"
          bgColor={color}
          color="white"
          coloredShadow={color}
          borderRadius="lg"
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="3rem"
          height="3rem"
          mt={-2}
        >
          <Icon fontSize="small" color="inherit">
            {icon}
          </Icon>
        </MDBox>
        <MDBox textAlign="right" lineHeight={1.25}>
          <MDTypography
            variant="button"
            fontWeight="light"
            color="text"
            fontSize="0.75rem"
          >
            {title}
          </MDTypography>
          <MDTypography variant="h5" fontWeight="bold">
            {value}
          </MDTypography>
        </MDBox>
      </MDBox>
      <Divider sx={{ my: 1 }} />
      <MDBox pb={1} px={2}>
        <MDTypography
          component="p"
          variant="caption"
          color="text"
          display="flex"
          alignItems="center"
        >
          {dateDisplay}
        </MDTypography>
      </MDBox>
    </Card>
  );

  return (
    <MDBox pt={2} pb={3}>
      {/* 1. Header Section */}
      <Card sx={{ mb: 3, p: 2, boxShadow: 2 }}>
        <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
          <Grid item>
            <MDTypography
              variant="h5"
              fontWeight="medium"
              textTransform="capitalize"
            >
              {placeData?.parkingCode} <span style={{ opacity: 0.5 }}>|</span>{" "}
              {reportLabel}
            </MDTypography>
            <MDTypography variant="button" color="text" display="block">
              {placeData?.locationAddress}
            </MDTypography>
            <MDBox display="flex" alignItems="center" gap={1} mt={0.5}>
              <Chip
                label={dateDisplay}
                size="small"
                variant="outlined"
                color="info"
              />
              {transactionType === "weekly" && (
                <Chip
                  label={moment(startDate).format("dddd")}
                  size="small"
                  color="secondary"
                />
              )}
            </MDBox>
          </Grid>
          <Grid item>
            <MDButton
              variant="gradient"
              color="info"
              size="small"
              onClick={downloadPdf}
            >
              Download PDF
            </MDButton>
          </Grid>
        </Grid>
      </Card>

      {/* 2. Minimal Synopsis Dashboard */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            icon="attach_money"
            title="Gross Revenue"
            value={`$${amountToShow(totalRevenue)}`}
            color="dark"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            icon="account_balance_wallet"
            title="Net Revenue"
            value={`$${amountToShow(netRevenue)}`}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            icon="receipt"
            title="Service Fees"
            value={`$${amountToShow(serviceFee)}`}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            icon="group"
            title={totalLabel}
            value={totalReservation}
            color="primary"
          />
        </Grid>
      </Grid>

      {/* 3. Compact Data Table */}
      <Card sx={{ boxShadow: 3 }}>
        <MDBox
          p={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <MDTypography variant="h6" fontWeight="medium">
            Revenue Breakdown
          </MDTypography>
        </MDBox>

        <MDBox>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small" aria-label="transaction report table">
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  <TableCell
                    sx={{
                      minWidth: 140,
                      fontWeight: "bold",
                      backgroundColor: "#f0f2f5",
                      py: 1.5,
                      border: "1px solid #f0f2f5",
                    }}
                  >
                    Rate Name
                  </TableCell>
                  {periods.map((p, i) => (
                    <TableCell
                      key={i}
                      align="right"
                      sx={{
                        minWidth: 70,
                        fontWeight: "bold",
                        backgroundColor: "#f0f2f5",
                        py: 1.5,
                        border: "1px solid #f0f2f5",
                      }}
                    >
                      {p.time}
                    </TableCell>
                  ))}
                  <TableCell
                    align="right"
                    sx={{
                      minWidth: 100,
                      fontWeight: "bold",
                      backgroundColor: "#f0f2f5",
                      py: 1.5,
                      border: "1px solid #f0f2f5",
                    }}
                  >
                    {totalLabel}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      minWidth: 130,
                      fontWeight: "bold",
                      backgroundColor: "#f0f2f5",
                      py: 1.5,
                      border: "1px solid #f0f2f5",
                    }}
                  >
                    Gross Revenue
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Rate Data Rows */}
                {Object.entries(rateTotals).map(([label, meta]) => (
                  <TableRow key={label} hover>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        fontWeight: "medium",
                        py: 1,
                        border: "1px solid #f0f2f5",
                      }}
                    >
                      {formatRateName(label)}
                    </TableCell>
                    {periods.map((p, idx) => (
                      <TableCell
                        key={idx}
                        align="right"
                        sx={{
                          color: "text.secondary",
                          py: 1,
                          border: "1px solid #f0f2f5",
                        }}
                      >
                        {p.rates?.[label]?.count || 0}
                      </TableCell>
                    ))}
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        py: 1,
                        border: "1px solid #f0f2f5",
                      }}
                    >
                      {meta.count}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        py: 1,
                        border: "1px solid #f0f2f5",
                      }}
                    >
                      ${amountToShow(meta.totalSum)}
                    </TableCell>
                  </TableRow>
                ))}

                {/* Styled Divider (Maintains Grid) */}
                <TableRow>
                  <TableCell
                    colSpan={periods.length + 3}
                    sx={{
                      p: 0,
                      height: "8px",
                      backgroundColor: "#f0f2f5",
                      border: "1px solid #f0f2f5",
                    }}
                  />
                </TableRow>

                {/* Summary Rows */}
                <TableRow hover sx={{ backgroundColor: "#f9fafb" }}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      py: 1,
                      border: "1px solid #f0f2f5",
                    }}
                  >
                    {totalLabel}
                  </TableCell>
                  {periods.map((p, idx) => (
                    <TableCell
                      key={idx}
                      align="right"
                      sx={{ py: 1, border: "1px solid #f0f2f5" }}
                    >
                      {p.totalCount}
                    </TableCell>
                  ))}
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      py: 1,
                      border: "1px solid #f0f2f5",
                    }}
                  >
                    {totalReservation}
                  </TableCell>
                  <TableCell sx={{ border: "1px solid #f0f2f5" }} />
                </TableRow>

                <TableRow hover>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "text.secondary",
                      py: 1,
                      border: "1px solid #f0f2f5",
                    }}
                  >
                    % of {totalLabel}
                  </TableCell>
                  {periods.map((p, idx) => (
                    <TableCell
                      key={idx}
                      align="right"
                      sx={{
                        color: "text.secondary",
                        py: 1,
                        border: "1px solid #f0f2f5",
                      }}
                    >
                      {Number(p.percentage || 0).toFixed(2)}%
                    </TableCell>
                  ))}
                  <TableCell sx={{ border: "1px solid #f0f2f5" }} />
                  <TableCell sx={{ border: "1px solid #f0f2f5" }} />
                </TableRow>

                <TableRow hover sx={{ backgroundColor: "#e8f5e9" }}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "success.dark",
                      py: 1,
                      border: "1px solid #f0f2f5",
                    }}
                  >
                    Total Revenue
                  </TableCell>
                  {periods.map((p, idx) => (
                    <TableCell
                      key={idx}
                      align="right"
                      sx={{
                        color: "success.dark",
                        fontWeight: "medium",
                        py: 1,
                        border: "1px solid #f0f2f5",
                      }}
                    >
                      ${amountToShow(p.totalRevenue)}
                    </TableCell>
                  ))}
                  <TableCell sx={{ border: "1px solid #f0f2f5" }} />
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      color: "success.dark",
                      py: 1,
                      border: "1px solid #f0f2f5",
                    }}
                  >
                    ${amountToShow(totalRevenue)}
                  </TableCell>
                </TableRow>

                <TableRow hover>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "text.secondary",
                      py: 1,
                      border: "1px solid #f0f2f5",
                    }}
                  >
                    % of Total Value
                  </TableCell>
                  {periods.map((p, idx) => (
                    <TableCell
                      key={idx}
                      align="right"
                      sx={{
                        color: "text.secondary",
                        py: 1,
                        border: "1px solid #f0f2f5",
                      }}
                    >
                      {Number(p.revenuePercent || 0).toFixed(2)}%
                    </TableCell>
                  ))}
                  <TableCell sx={{ border: "1px solid #f0f2f5" }} />
                  <TableCell sx={{ border: "1px solid #f0f2f5" }} />
                </TableRow>

                <TableRow hover>
                  <TableCell
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 1,
                      border: "1px solid #f0f2f5",
                    }}
                  >
                    Total Service Fee
                  </TableCell>
                  {periods.map((p, idx) => (
                    <TableCell
                      key={idx}
                      align="right"
                      sx={{
                        color: "text.secondary",
                        py: 1,
                        border: "1px solid #f0f2f5",
                      }}
                    >
                      ${amountToShow(p.totalServiceFee)}
                    </TableCell>
                  ))}
                  <TableCell sx={{ border: "1px solid #f0f2f5" }} />
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 1,
                      border: "1px solid #f0f2f5",
                    }}
                  >
                    ${amountToShow(serviceFee)}
                  </TableCell>
                </TableRow>

                <TableRow hover sx={{ backgroundColor: "#e3f2fd" }}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "info.main",
                      py: 1,
                      border: "1px solid #f0f2f5",
                    }}
                  >
                    Net Revenue
                  </TableCell>
                  {periods.map((p, idx) => (
                    <TableCell
                      key={idx}
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        color: "info.main",
                        py: 1,
                        border: "1px solid #f0f2f5",
                      }}
                    >
                      $
                      {amountToShow(
                        (p.totalRevenue || 0) - (p.totalServiceFee || 0),
                      )}
                    </TableCell>
                  ))}
                  <TableCell sx={{ border: "1px solid #f0f2f5" }} />
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      color: "info.main",
                      py: 1,
                      border: "1px solid #f0f2f5",
                    }}
                  >
                    ${amountToShow(netRevenue)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </MDBox>
      </Card>
    </MDBox>
  );
};

export default TransactionReportTable;
